import { db } from '../firebase'
import {
  collection, doc, setDoc, updateDoc, getDoc, query,
  where, getDocs, onSnapshot, deleteDoc, deleteField, arrayUnion, arrayRemove, serverTimestamp,
  getDocFromServer, disableNetwork, enableNetwork,
} from 'firebase/firestore'

const CHARS = 'ABCDEFGHJKMNPQRSTUVWXYZ23456789' // no ambiguous 0,O,I,1,L

export function generatePartyCode() {
  return Array.from({ length: 6 }, () => CHARS[Math.floor(Math.random() * CHARS.length)]).join('')
}

export async function createParty(hostId, mode, hostPlayer) {
  const code = generatePartyCode()
  const partyRef = doc(collection(db, 'parties'))
  await setDoc(partyRef, {
    code,
    status: 'lobby',
    mode,
    createdAt: serverTimestamp(),
    hostId,
    directorId: null,
    players: {},  // Host is never a player — players only join via code
    teams: {},
    teamCount: null,
    teamsLocked: false,
    startActor: null,
    goalActor: null,
    actorPickerIds: [],
    ticketsCost: 1,
    chargedPlayers: [],
    participantUids: [hostId], // includes host + all joined players (for session recovery)
  })
  return { partyId: partyRef.id, code }
}

export async function joinParty(code, player) {
  const q = query(collection(db, 'parties'), where('code', '==', code.toUpperCase()))
  const snap = await getDocs(q)
  if (snap.empty) return { error: 'not_found' }
  const partyDoc = snap.docs[0]
  const partyData = partyDoc.data()
  if (partyData.status !== 'lobby') return { error: 'already_started' }
  const partyId = partyDoc.id
  // Charge the player at lobby join → record in chargedPlayers so refunds are possible.
  // joinedAt as Date (serverTimestamp not allowed inside map values).
  await updateDoc(doc(db, 'parties', partyId), {
    [`players.${player.uid}`]: {
      username: player.username || 'Guest',
      avatar: player.avatar || null,
      isHost: false,
      teamId: null,
      ready: false,
      joinedAt: new Date(),
    },
    participantUids: arrayUnion(player.uid),
  })
  return { partyId, code: partyData.code, mode: partyData.mode }
}

/**
 * Remove a player from a lobby (player chose to leave before game start).
 * Returns whether the player was charged (caller refunds ticket if true).
 */
export async function leaveParty(partyId, uid) {
  const ref = doc(db, 'parties', partyId)
  const snap = await getDoc(ref)
  if (!snap.exists()) return { refunded: false }
  const data = snap.data()
  // Allow leaving (with potential refund) during lobby, pre-start, or actor_pick.
  // Once the game is actually playing (countdown/playing/etc.), no refund is issued.
  const refundEligibleStatuses = new Set(['lobby', 'pre-start', 'actor_pick'])
  if (!refundEligibleStatuses.has(data.status)) return { refunded: false }
  const wasCharged = (data.chargedPlayers || []).includes(uid)
  await updateDoc(ref, {
    [`players.${uid}`]: deleteField(),
    chargedPlayers: arrayRemove(uid),
    participantUids: arrayRemove(uid),
  })
  return { refunded: wasCharged }
}

/**
 * Host ends the party from the lobby — return the list of player UIDs that were
 * charged so the caller can refund each one client-side via Firestore.
 * (Each device refunds itself by reading chargedPlayers, since one client can't
 * directly mutate another user's tickets.)
 */
export async function endPartyFromLobby(partyId) {
  const ref = doc(db, 'parties', partyId)
  const snap = await getDoc(ref)
  if (!snap.exists()) return { chargedPlayers: [] }
  const data = snap.data()
  const charged = data.chargedPlayers || []
  // Mark status as 'ended' — every player's device sees this and refunds themselves
  await updateDoc(ref, { status: 'ended', endedReason: 'host_cancelled' })
  return { chargedPlayers: charged }
}

export function subscribeToParty(partyId, callback) {
  return onSnapshot(doc(db, 'parties', partyId), (snap) => {
    callback(snap.exists() ? { id: snap.id, ...snap.data() } : null)
  })
}

export async function updateParty(partyId, updates) {
  await updateDoc(doc(db, 'parties', partyId), updates)
}

// ── Stale-listener recovery ───────────────────────────────────────────────────
// Firestore onSnapshot listeners can go silently stale on mobile webviews (the OS
// suspends the connection on background / a network blip) and miss a status flip,
// stranding a player on a passive screen until something forces a reconnect. These
// two helpers back the App-level watchdog.

/** One-shot SERVER read of a party doc (bypasses a possibly-stale listener AND the
 *  local cache). Returns the same `{ id, ...data }` shape as subscribeToParty, or
 *  null if the doc is gone. Throws if genuinely offline — callers should ignore and
 *  retry on the next tick. */
export async function getPartyFromServer(partyId) {
  const snap = await getDocFromServer(doc(db, 'parties', partyId))
  return snap.exists() ? { id: snap.id, ...snap.data() } : null
}

/** Force the Firestore SDK to drop and re-establish its connection. This restarts
 *  every active listen stream (a one-shot get does NOT reliably restart a stale
 *  persistent onSnapshot), so real-time delivery resumes. Use on app-foreground /
 *  network-return only — it briefly toggles the whole SDK's network. Non-fatal. */
export async function resyncFirestore() {
  try {
    await disableNetwork(db)
    await enableNetwork(db)
  } catch { /* non-fatal — the proactive poll still advances the UI */ }
}

export async function assignDirector(partyId) {
  const snap = await getDoc(doc(db, 'parties', partyId))
  if (!snap.exists()) return null
  const playerIds = readyPlayerIds(snap.data().players)
  if (!playerIds.length) return null
  const directorId = playerIds[Math.floor(Math.random() * playerIds.length)]
  await updateDoc(doc(db, 'parties', partyId), { directorId, status: 'director_reveal' })
  return directorId
}

export async function assignActorPickers(partyId) {
  const snap = await getDoc(doc(db, 'parties', partyId))
  if (!snap.exists()) return []
  const playerIds = readyPlayerIds(snap.data().players)
  const shuffled = [...playerIds].sort(() => Math.random() - 0.5)
  const actorPickerIds = shuffled.slice(0, Math.min(2, shuffled.length))
  await updateDoc(doc(db, 'parties', partyId), { actorPickerIds, status: 'actor_pick' })
  return actorPickerIds
}

/**
 * H2H-aware actor-picker assignment. Tries to pick one player from each of the
 * first two teams so both sides have a representative in the blind pick.
 * Falls back to random across all players if a team can't supply a picker
 * (e.g. 7v1 where the lone team-2 player has somehow exited).
 */
export async function assignH2HActorPickers(partyId) {
  const snap = await getDoc(doc(db, 'parties', partyId))
  if (!snap.exists()) return []
  const data = snap.data()
  const teams = Array.isArray(data.teams) ? data.teams : []
  const actorPickerIds = []
  if (teams.length >= 2) {
    const t0 = (teams[0]?.playerIds || []).filter(Boolean)
    const t1 = (teams[1]?.playerIds || []).filter(Boolean)
    if (t0.length) actorPickerIds.push(t0[Math.floor(Math.random() * t0.length)])
    if (t1.length) actorPickerIds.push(t1[Math.floor(Math.random() * t1.length)])
  }
  // Fallback: ensure exactly 2 unique pickers (CONFIRMED players only)
  if (actorPickerIds.length < 2) {
    const allIds = readyPlayerIds(data.players)
    const shuffled = [...allIds].sort(() => Math.random() - 0.5)
    for (const uid of shuffled) {
      if (!actorPickerIds.includes(uid) && actorPickerIds.length < 2) {
        actorPickerIds.push(uid)
      }
    }
  }
  await updateDoc(doc(db, 'parties', partyId), { actorPickerIds, status: 'actor_pick' })
  return actorPickerIds
}

export async function deleteParty(partyId) {
  await deleteDoc(doc(db, 'parties', partyId))
}

// ── The Trilogy ────────────────────────────────────────────────────────────
//
// Firestore state shape for a Trilogy party (stored under /parties/{partyId}):
//
// {
//   // — inherited from createParty() —
//   code:            string          // 6-char join code
//   mode:            'trilogy'
//   status:          'lobby' | 'actor_pick' | 'reveal' | 'instructions' |
//                    'countdown' | 'trilogy-playing' | 'round_standings' |
//                    'final_standings'
//   hostId:          string
//   players:         { [uid]: { username, avatar, isHost, ready, joinedAt } }
//   actorPickerIds:  string[]        // 2 randomly selected uids for the CURRENT round
//   startActor:      Actor | null    // puzzle start for the current round
//   goalActor:       Actor | null    // puzzle goal  for the current round
//   chargedPlayers:  string[]
//   participantUids: string[]
//
//   // — Trilogy-specific —
//   currentRound:    1 | 2 | 3
//
//   rounds: {
//     1: {
//       startActor:    Actor | null,
//       goalActor:     Actor | null,
//       actorPickerIds: string[],
//       results: {
//         [uid]: { hops: number, timeMs: number, completed: boolean }
//       }
//     },
//     2: { ... },   // same shape
//     3: { ... },   // same shape
//   }
//
//   cumulativeScores: {
//     [uid]: { totalHops: number, totalTimeMs: number }
//   }
// }

/**
 * Creates a new Trilogy party in Firestore.
 * Calls the shared createParty() for the base document (code, lobby status,
 * players map, etc.) then patches in the Trilogy-specific fields.
 */
export async function createTrilogyParty(hostId, hostPlayer) {
  const { partyId, code } = await createParty(hostId, 'trilogy', hostPlayer)
  await updateDoc(doc(db, 'parties', partyId), {
    currentRound: 1,
    rounds: {
      1: { startActor: null, goalActor: null, actorPickerIds: [], results: {} },
      2: { startActor: null, goalActor: null, actorPickerIds: [], results: {} },
      3: { startActor: null, goalActor: null, actorPickerIds: [], results: {} },
    },
    cumulativeScores: {},
  })
  return { partyId, code }
}

/**
 * Find any non-ended party where this UID is a participant (host or player).
 * Used for silent session recovery on app load.
 * Returns the most recently created match, or null.
 */
export async function findActivePartyForUser(uid) {
  if (!uid) return null
  try {
    const q = query(
      collection(db, 'parties'),
      where('participantUids', 'array-contains', uid),
    )
    const snap = await getDocs(q)
    if (snap.empty) return null
    const FIFTEEN_MIN_MS = 15 * 60 * 1000
    const TEN_MIN_MS     = 10 * 60 * 1000
    const FIVE_MIN_MS    = 5  * 60 * 1000
    const THIRTY_MIN_MS  = 30 * 60 * 1000
    const now = Date.now()
    // Read a Firestore Timestamp field as millis (Timestamp instance OR {seconds}).
    const tsMs = (v) => v?.toMillis?.() ?? (v?.seconds ?? 0) * 1000

    const active = snap.docs
      .map((d) => ({ id: d.id, ...d.data() }))
      .filter((p) => {
        // 'ended' and 'final_standings' are terminal — never auto-rejoin them.
        // (Trilogy's final results is game-over; recovery should fall through to
        // home. trilogy-playing / round_standings stay rejoinable below.)
        if (!p.status || p.status === 'ended' || p.status === 'final_standings') return false
        // Each active status pairs a TTL with the timestamp field it's measured against.
        // lobby / pre-start                                           → 15 min from createdAt
        // actor_pick / reaction_test / team_setup / team_locked       → 10 min from createdAt
        // reveal / countdown                                          →  5 min from createdAt
        // h2h-playing                                                 → 30 min from h2hTurnStartedAt
        // co-op-playing                                               → 30 min from turnStartedAt
        // playing (BR/SR) / results / coop-results / h2h-results      → never expire
        let ttl = null
        let timeMs = null
        if (p.status === 'lobby' || p.status === 'pre-start') {
          ttl = FIFTEEN_MIN_MS; timeMs = tsMs(p.createdAt)
        } else if (p.status === 'actor_pick' || p.status === 'reaction_test'
                || p.status === 'team_setup' || p.status === 'team_locked') {
          ttl = TEN_MIN_MS; timeMs = tsMs(p.createdAt)
        } else if (p.status === 'reveal' || p.status === 'countdown') {
          ttl = FIVE_MIN_MS; timeMs = tsMs(p.createdAt)
        } else if (p.status === 'instructions') {
          ttl = TEN_MIN_MS; timeMs = tsMs(p.createdAt)
        } else if (p.status === 'director_selection' || p.status === 'casting') {
          ttl = TEN_MIN_MS; timeMs = tsMs(p.createdAt)
        } else if (p.status === 'director_reveal') {
          ttl = FIVE_MIN_MS; timeMs = tsMs(p.createdAt)
        } else if (p.status === 'dc-playing') {
          ttl = THIRTY_MIN_MS; timeMs = tsMs(p.dcStepStartedAt) || tsMs(p.createdAt)
        } else if (p.status === 'h2h-playing') {
          // Fall back to createdAt if the turn timestamp is missing for any reason
          ttl = THIRTY_MIN_MS; timeMs = tsMs(p.h2hTurnStartedAt) || tsMs(p.createdAt)
        } else if (p.status === 'co-op-playing') {
          ttl = THIRTY_MIN_MS; timeMs = tsMs(p.turnStartedAt) || tsMs(p.createdAt)
        }
        if (ttl !== null && timeMs && now - timeMs > ttl) return false
        return true
      })
    if (active.length === 0) return null
    // Sort by createdAt desc — most recent active party wins
    active.sort((a, b) => {
      const at = a.createdAt?.toMillis?.() ?? 0
      const bt = b.createdAt?.toMillis?.() ?? 0
      return bt - at
    })
    return active[0]
  } catch (err) {
    console.error('[partyGame] findActivePartyForUser:', err)
    return null
  }
}

/**
 * Transition to 'pre-start': reset all players' ready flags so fresh
 * confirmations are required, then set status so player devices show the
 * ticket-confirmation prompt.
 */
export async function initiateStart(partyId, playerUids) {
  // preStartStartedAt anchors the 30s Ready Up countdown on every device.
  const updates = { status: 'pre-start', preStartStartedAt: serverTimestamp() }
  for (const uid of playerUids) {
    updates[`players.${uid}.ready`] = false
  }
  await updateDoc(doc(db, 'parties', partyId), updates)
}

/** CONFIRMED (ready) player uids — the active roster for a starting round. A
 *  timed/early start drops un-ready (unconfirmed, uncharged) players, so picker
 *  and director assignment below run over this set only. */
function readyPlayerIds(players = {}) {
  return Object.keys(players).filter((uid) => players[uid]?.ready === true)
}

/**
 * Player confirms they're ready to start the game.
 * This is the moment the ticket is officially committed — add to chargedPlayers
 * so the host-cancel refund signal can reach this player if needed.
 */
export async function confirmPlayerStart(partyId, uid) {
  await updateDoc(doc(db, 'parties', partyId), {
    [`players.${uid}.ready`]: true,
    chargedPlayers: arrayUnion(uid),
  })
}

export async function findPartyByCode(code) {
  const q = query(collection(db, 'parties'), where('code', '==', code.toUpperCase()))
  const snap = await getDocs(q)
  if (snap.empty) return null
  const d = snap.docs[0]
  return { id: d.id, ...d.data() }
}
